import React, { useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store/rootReducer";
import "react-datepicker/dist/react-datepicker.css";
import DatePicker from "react-datepicker";
import { showModal } from "../../../reducers/modal/modal.actions";
import moment from "moment";
import RichTextEditor, { EditorValue } from 'react-rte';
import { DOWNLOAD_MONEV } from "../../../store/api";
import { deleteFile, updateDeskripsi, updateEvaluasi, updateFile, updateLokasi, updateNamaKegiatan, updateTanggalMulai, updateTanggalSelesai, updateTautan } from "../../../reducers/monev/monev.actions";
import { fieldErrorMessage, fieldHasErrors } from "../../../store/errorHandler";
import { PermissionMenuName } from "../../../interfaces/UserRole";
import { allowSave } from "../../../store/helpers";

const toolbarConfig: any = {
  display: ['INLINE_STYLE_BUTTONS', 'BLOCK_TYPE_BUTTONS', 'LINK_BUTTONS', 'BLOCK_TYPE_DROPDOWN', 'HISTORY_BUTTONS'],
  INLINE_STYLE_BUTTONS: [
    {label: 'Bold', style: 'BOLD'},
    {label: 'Italic', style: 'ITALIC'},
    {label: 'Underline', style: 'UNDERLINE'}
  ],
  BLOCK_TYPE_DROPDOWN: [
    {label: 'Normal', style: 'unstyled'},
    {label: 'Heading Largest', style: 'header-one'},
    {label: 'Heading Large', style: 'header-two'},
    {label: 'Heading Medium', style: 'header-three'},
    {label: 'Heading Normal', style: 'header-four'},
    {label: 'Heading Small', style: 'header-five'},
    {label: 'Heading Smallest', style: 'header-six'},
  ],
  BLOCK_TYPE_BUTTONS: [
    {label: 'UL', style: 'unordered-list-item'},
    {label: 'OL', style: 'ordered-list-item'}
  ]
};

export const FormDetail = () => {
  const dispatch = useDispatch();
  const fileInput = useRef<HTMLInputElement>(null);
  const errors = useSelector((state: RootState) => state.monev.error);
  const isLoading = useSelector((state: RootState) => state.monev.isLoading);
  const detailMonev = useSelector((state: RootState) => state.monev.selectedMonev);
  const selectedFile = useSelector((state: RootState) => state.monev.selectedFile);
  const editorEvaluasi = useSelector((state: RootState) => state.monev.editorEvaluasi);
  const editorDeskripsi = useSelector((state: RootState) => state.monev.editorDeskripsi);
  const role = useSelector((state: RootState) => state.authorization.systemAccess[PermissionMenuName.monevPartner].ACCESS);

  const canSave = allowSave(role, 3);

  const onChangeTanggalMulai = (date: Date) => {
    if (!detailMonev) return;
    if (moment(date).diff(detailMonev.TANGGAL_SELESAI_KEGIATAN) > 0) {
      dispatch(showModal('Proses mengganti tanggal gagal', 'Tanggal mulai harus lebih kecil dari tanggal selesai'));
      return;
    }
    dispatch(updateTanggalMulai(moment(date).format('YYYY-MM-DD')));
  }

  const onChangeTanggalSelesai = (date: Date) => {
    if (!detailMonev) return;
    if (moment(date).diff(detailMonev.TANGGAL_MULAI_KEGIATAN) < 0) {
      dispatch(showModal('Proses mengganti tanggal gagal', 'Tanggal selesai harus lebih besar dari tanggal mulai'));
      return;
    }
    dispatch(updateTanggalSelesai(moment(date).format('YYYY-MM-DD')));
  }

  const onChangeNamaKegiatan = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(updateNamaKegiatan(e.target.value));
  }

  const onChangeLokasi = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(updateLokasi(e.target.value));
  }

  const onChangeDeskripsi = (e: EditorValue) => {
    dispatch(updateDeskripsi(e));
  }

  const onChangeEvaluasi = (e: EditorValue) => {
    dispatch(updateEvaluasi(e));
  }

  const onChangeTautan = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(updateTautan(e.target.value));
  }

  const addDokumen = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      dispatch(updateFile(file));
    }
  }

  const download = (url: string) => {
    window.open(`${DOWNLOAD_MONEV}${url}`, "_blank");
  }

  const clearFileData = () => {
    dispatch(deleteFile());
  }

  const cancelChanges = () => {
    if (fileInput && fileInput.current) {
      fileInput.current.value = "";
    }
    dispatch(updateFile(null));
  }

  if (!detailMonev) return (
    <React.Fragment>
      <div className="box">
        <div className="box-header with-border">
          <h3 className="box-title">Detail Kerjasama</h3>
        </div>
        <div className="box-body">
          Sedang memuat data...
        </div>
        <div className="overlay active">
          <i className="fa fa-refresh fa-spin"></i>
        </div>
      </div>
    </React.Fragment>
  );

  return (
    <React.Fragment>
      <div className="box">
        <div className="box-header with-border">
          <h3 className="box-title">Detail Kerjasama</h3>
          <div className="box-tools pull-right"></div>
        </div>
        <div className="box-body">
          <div className={`form-group ${fieldHasErrors('NAMA_KEGIATAN', errors) ? 'has-error' : ''}`}>
            <label>Nama Kegiatan <span className="text-red"><b>*</b></span></label>
            <input
              type="text"
              className="form-control"
              disabled={isLoading || !canSave}
              onChange={onChangeNamaKegiatan}
              value={detailMonev.NAMA_KEGIATAN}
            />
            <span className="help-block">{ fieldErrorMessage('NAMA_KEGIATAN', errors) }</span>
          </div>
          <div className={`form-group ${fieldHasErrors('LOKASI_KEGIATAN', errors) ? 'has-error' : ''}`}>
            <label>Lokasi <span className="text-red"><b>*</b></span></label>
            <input
              type="text"
              className="form-control"
              disabled={isLoading || !canSave}
              onChange={onChangeLokasi}
              value={detailMonev.LOKASI_KEGIATAN}
            />
            <span className="help-block">{ fieldErrorMessage('LOKASI_KEGIATAN', errors) }</span>
          </div>
          <div className={`form-group ${fieldHasErrors('TANGGAL_MULAI_KEGIATAN', errors) ? 'has-error' : ''}`}>
            <label>Tanggal Mulai <span className="text-red"><b>*</b></span></label>
            <DatePicker
              className="form-control"
              disabled={isLoading || !canSave}
              selected={moment(detailMonev.TANGGAL_MULAI_KEGIATAN).toDate()}
              onChange={onChangeTanggalMulai}
            />
            <span className="help-block">{ fieldErrorMessage('TANGGAL_MULAI_KEGIATAN', errors) }</span>
          </div>
          <div className={`form-group ${fieldHasErrors('TANGGAL_SELESAI_KEGIATAN', errors) ? 'has-error' : ''}`}>
            <label>Tanggal Selesai <span className="text-red"><b>*</b></span></label>
            <DatePicker
              className="form-control"
              disabled={isLoading || !canSave}
              selected={moment(detailMonev.TANGGAL_SELESAI_KEGIATAN).toDate()}
              onChange={onChangeTanggalSelesai}
            />
            <span className="help-block">{ fieldErrorMessage('TANGGAL_SELESAI_KEGIATAN', errors) }</span>
          </div>
          <div className={`form-group ${fieldHasErrors('DESCR_KEGIATAN', errors) ? 'has-error' : ''}`}>
            <label>Deskripsi Kegiatan<span className="text-red"><b>*</b></span></label>
            <RichTextEditor
              value={editorDeskripsi}
              disabled={isLoading || !canSave}
              onChange={onChangeDeskripsi}
              toolbarConfig={toolbarConfig}
              editorStyle={{ height: '300px' }}
            />
            <span className="help-block">{ fieldErrorMessage('DESCR_KEGIATAN', errors) }</span>
          </div>
          <div className={`form-group ${fieldHasErrors('EVALUASI_KEGIATAN', errors) ? 'has-error' : ''}`}>
            <label>Evaluasi<span className="text-red"><b>*</b></span></label>
            <RichTextEditor
              value={editorEvaluasi}
              onChange={onChangeEvaluasi}
              toolbarConfig={toolbarConfig}
              disabled={isLoading || !canSave}
              editorStyle={{ height: '300px' }}
            />
            <span className="help-block">{ fieldErrorMessage('EVALUASI_KEGIATAN', errors) }</span>
          </div>
          <div className={`form-group ${fieldHasErrors('FILE_KEGIATAN', errors) ? 'has-error' : ''}`}>
            <label>Dokumentasi (pdf / zip)</label>
            <input
              ref={fileInput}
              type="file"
              disabled={isLoading || !canSave}
              onChange={addDokumen}
              className="form-control"
              accept=".pdf,zip"
              placeholder="Unggah dokumentasi (pdf/zip)"
            />
            <div style={{ marginTop: '5px' }}>
              { detailMonev.FILE_KEGIATAN ? (
                  <React.Fragment>
                    <button type="button" className="btn btn-primary" disabled={isLoading} onClick={() => download(detailMonev.FILE_KEGIATAN)}>
                      Unduh Berkas
                    </button>
                    &nbsp;
                    <button type="button" className="btn btn-danger" disabled={isLoading || !canSave} onClick={() => clearFileData()}>
                      Hapus Berkas
                    </button>
                  </React.Fragment>
                ) : null
              }

              {
                selectedFile ? (
                  <React.Fragment>
                    &nbsp;
                    <button type="button" className="btn btn-danger" disabled={isLoading || !canSave} onClick={() => cancelChanges()}>
                      Batalkan Perubahan
                    </button>
                  </React.Fragment>
                ) : null
              }
            </div>
            <span className="help-block">{ fieldErrorMessage('FILE_KEGIATAN', errors) }</span>
          </div>
          <div className={`form-group ${fieldHasErrors('LINK_DOKUMENTASI_KEGIATAN', errors) ? 'has-error' : ''}`}>
            <label>Tautan Dokumentasi Tambahan</label>
            <input
              type="text"
              disabled={isLoading || !canSave}
              className="form-control"
              onChange={onChangeTautan}
              value={detailMonev.LINK_DOKUMENTASI_KEGIATAN}
            />
            <span className="help-block">{ fieldErrorMessage('LINK_DOKUMENTASI_KEGIATAN', errors) }</span>
          </div>
        </div>
        <div className={`overlay ${isLoading ? 'active' : null}`}>
          <i className="fa fa-refresh fa-spin"></i>
        </div>
      </div>
    </React.Fragment>
  );
};
